import React from 'react';
import { Mic, Users, Calendar, Award, Lock } from 'lucide-react';
import { getProgress } from './badgeConfig';

export default function BadgePreview({ balance }) {
  const hostCount = balance?.lifetime_host_count || 0;
  const inviteCount = balance?.lifetime_invite_count || 0;
  const attendCount = balance?.lifetime_attend_count || 0;

  const hostProgress = getProgress('host', hostCount);
  const inviteProgress = getProgress('invite', inviteCount);
  const attendProgress = getProgress('attend', attendCount);

  const BadgeCard = ({ icon: Icon, title, count, progress, color }) => {
    const { current, next, progress: progressPercent, remaining } = progress;

    return (
      <div className="bg-white/5 border border-white/10 rounded-xl">
        <div className="p-4 space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Icon className={`w-5 h-5 ${color}`} />
              <span className="text-white font-medium text-sm">{title}</span>
            </div>
            <span className="text-gray-400 text-xs">{count} total</span>
          </div>

          {/* Current Badge */}
          {current && (
            <div className="flex items-start gap-2 p-2 bg-white/5 rounded-lg border border-white/10">
              <Award className={`w-4 h-4 ${color} mt-0.5`} />
              <div className="flex-1 min-w-0">
                <p className="text-white text-sm font-semibold">{current.name}</p>
                <p className="text-gray-400 text-xs italic">{current.vibe}</p>
              </div>
            </div>
          )}

          {/* Next Badge Progress */}
          {next ? (
            <div className="space-y-2">
              <div className="flex items-center justify-between text-xs">
                <span className="text-gray-400">Next badge:</span>
                <span className="text-gray-300">{remaining} more needed</span>
              </div>
              <div className="flex items-start gap-2 p-2 bg-black/20 rounded-lg border border-dashed border-white/20">
                <Lock className="w-4 h-4 text-gray-500 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <p className="text-gray-300 text-sm font-medium">{next.name}</p>
                  <p className="text-gray-500 text-xs italic">{next.vibe}</p>
                </div>
              </div>
              <div className="w-full h-2 bg-white/10 rounded-full overflow-hidden">
                <div
                  className="h-full bg-[#8b5cf6] rounded-full transition-all"
                  style={{ width: `${progressPercent}%` }}
                />
              </div>
              <p className="text-xs text-center text-gray-400">
                {Math.round(progressPercent)}% to {next.name}
              </p>
            </div>
          ) : (
            <div className="text-center py-2">
              <p className="text-green-400 text-sm font-semibold">Max Level Achieved!</p>
              <p className="text-gray-400 text-xs mt-1">You've unlocked all {title.toLowerCase()} badges</p>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-white font-semibold">Badge Progress</h3>
        <Award className="w-5 h-5 text-[#8b5cf6]" />
      </div>

      <div className="grid grid-cols-1 gap-3">
        <BadgeCard
          icon={Mic}
          title="Host Badges"
          count={hostCount}
          progress={hostProgress}
          color="text-[#8b5cf6]"
        />
        <BadgeCard
          icon={Users}
          title="Invite Badges"
          count={inviteCount}
          progress={inviteProgress}
          color="text-[#00c853]"
        />
        <BadgeCard
          icon={Calendar}
          title="Attend Badges"
          count={attendCount}
          progress={attendProgress}
          color="text-[#3b82f6]"
        />
      </div>
    </div>
  );
}
